import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { LineSeriesType } from '@mui/x-charts';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { UserContext } from '../../../../../context/UserContext';
import { formatValue, getUniqueProperties } from '../../../../../helpers/helpers';
import { Bucket } from '../../../../../api/indicators';
import { DashboardFiltersContext } from '../../filters/DashboardFiltersContext';
import AreaChartError from './AreaChartError';
import AreaChartSkeleton from './AreaChartSkeleton';
import AreaChartBase, { AreaChartDataItem } from './AreaChartBase';
import ChartNoData from '../ChartNoData';

export const selectStyle: CSSProperties = {
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  width: '210px',
};

export interface AreaChartProps {
    title: string;
    currency?: string;
    description: string;
    textLink?: string;
    linkChart?: string;
    isLoading: boolean;
    isError: boolean;
    leftAxisLabel: string;
    rightAxisLabel?: string;
    lineLabels?: string[];
    data: AreaChartDataItem[];
    bucket: Bucket;
    setBucket: (bucket: Bucket) => void;
    height: number;
    isPercentage?: boolean;
}

const AreaChart = (
    {
        title,
        currency,
        description,
        isLoading,
        isError,
        leftAxisLabel,
        rightAxisLabel,
        textLink,
        linkChart,
        lineLabels,
        data,
        bucket,
        setBucket,
        height,
        isPercentage = false
    }: AreaChartProps
) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useContext(UserContext);
    const [areaPlotSeries, setAreaPlotSeries] = useState<LineSeriesType[]>([]);
    const colours: string[] = useMemo(() => [
      theme.palette.blue.main,
      theme.palette.cyan.main,
      theme.palette.teal.main,
      theme.palette.lightGreen.main,
      theme.palette.lime.main,
      theme.palette.yellow.main,
      theme.palette.orange.main,
      theme.palette.deepOrange.main,
      theme.palette.fucsia.main,
      theme.palette.purple.main,
  ], [theme]);
    const { openFilters } = React.useContext(DashboardFiltersContext);

    const buildAreaPlotSeries = useCallback(
      (dataProperties: string[]): LineSeriesType[] =>
          dataProperties.map((lineKey, index) => ({
              id: lineKey,
              label: lineLabels ? lineLabels[index] : lineKey,
              type: 'line',
              dataKey: lineKey,
              curve: 'linear',
              color: colours[index % colours.length],
              valueFormatter: (v: number | null) => formatValue(v, true, currency, locale, undefined, false, false, isPercentage),
              area: true
          })),
      [lineLabels, colours, currency, locale, isPercentage]
  );
    
    const noData = !data?.length;

    useEffect(() => {
        if (noData) return;
        const dataProperties = getUniqueProperties(data, ['dateTime']);
        const areaSeries = buildAreaPlotSeries(dataProperties);
        setAreaPlotSeries(areaSeries);
    }, [data, buildAreaPlotSeries, noData]);

    return (
            <Card
                elevation={0}
                sx={{
                    minWidth: '200px',
                    border: `solid 1px`,
                    borderColor: theme.palette.grey['12p'],
                    marginX: '8px',
                    padding: '12px 16px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid'
                }}
            >
        {isLoading && <AreaChartSkeleton />}
        {!isLoading && (
          <>
            {isError && <AreaChartError title={title} description={description} bucket={bucket} setBucket={setBucket}/>}
            {!isError && noData && <ChartNoData height={height}  title={title} description={description} openFilters={openFilters} />}
            {!isError && !noData && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginY: '6px' }}>
                  <Box>
                    <Typography variant="h2">{title}</Typography>
                    <Typography variant='body2'>{description}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <TextField
                      size="medium"
                      sx={selectStyle}
                      select
                      label={t("screens.dashboarding.charts.showBy")}
                      value={bucket}
                      id="period"
                      onChange={(event) => setBucket(event.target.value as Bucket)}
                      inputProps={{ 'data-qa-id': "filterPeriodOption" }}
                    >
                      <MenuItem value="hour">{t('components.select.hour')}</MenuItem>
                      <MenuItem value="day">{t('components.select.days')}</MenuItem>
                      <MenuItem value="week">{t('components.select.week')}</MenuItem>
                    </TextField>
                  </Box>
                </Box>
                <AreaChartBase bucket={bucket} series={areaPlotSeries} data={data} height={height} leftAxisLabel={leftAxisLabel} rightAxisLabel={rightAxisLabel} />
                {!!linkChart && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                    <Button href={linkChart} sx={{ fontSize: '16px', fontWeight: '600', color: 'primary.main' }}>
                      {textLink}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Card>
  );
};

export default AreaChart;
